/*
  The pictures Word and classic Outlook put on the clipboard, read out of its RTF.

  Their clipboard HTML points every <img> at a file:/// path in the copying machine's temp folder,
  which no web page may read. But the same selection also goes on the clipboard as RTF, and there
  Office writes each picture out in full: a {\pict ...} group naming the picture's format with a
  control word (\pngblip, \jpegblip, \emfblip, ...) followed by its bytes as hex.

  So the pictures are read here in document order, each with the size it is shown at, for the
  paste handler to match to the file: images in the HTML (which carry the same size). What keeps
  that matching honest:

    * {\nonshppict ...} and {\shprslt ...} are skipped whole. They are fallbacks Office writes for
      older readers - a second copy of a picture already given (Word writes a WMF of every picture
      there) - and reading them would offer each picture twice.
    * a picture in a format no browser can show (an EMF, say) stays in the list as unusable rather
      than being dropped from it, so its <img> is matched to it - and dropped - instead of taking
      the next picture's.
    * a drawn shape has no {\pict} at all, only an <img> in the HTML: nothing here matches its
      size, so it is dropped rather than shifting every later picture along by one.
*/

export interface IRtfPicture {
  // null: a format the browser cannot show, or data that did not hold up.
  contentType: 'image/png' | 'image/jpeg' | null;
  bytes: Uint8Array | null;
  // The size it is shown at, in CSS pixels - what Word's HTML gives as width/height.
  width?: number;
  height?: number;
}

// 1440 twips to the inch, 96 CSS pixels to the inch.
const TWIPS_PER_PIXEL = 15;

// \picwgoal / \pichgoal are the picture's shown size in twips, before \picscalex / \picscaley (a
// percentage) and less any cropping. Absent a goal there is nothing reliable to size it by.
function shownSize(own: string): Pick<IRtfPicture, 'width' | 'height'> {
  const number = (word: string, fallback: number) => {
    const match = new RegExp(`\\\\${word}(-?\\d+)`).exec(own);
    return match ? Number(match[1]) : fallback;
  };

  const goalWidth = number('picwgoal', 0);
  const goalHeight = number('pichgoal', 0);
  if (!goalWidth || !goalHeight) return {};

  const width =
    ((goalWidth - number('piccropl', 0) - number('piccropr', 0)) * number('picscalex', 100)) / 100;
  const height =
    ((goalHeight - number('piccropt', 0) - number('piccropb', 0)) * number('picscaley', 100)) / 100;

  return { width: width / TWIPS_PER_PIXEL, height: height / TWIPS_PER_PIXEL };
}

const SKIPPED_GROUPS = ['nonshppict', 'shprslt'];

// "{\pict\picw..." -> "pict"; "{\*\shppict{..." -> "shppict". A destination's optional \* is looked past.
const GROUP_WORD = /^\{(?:\\\*)?\\([a-zA-Z]+)/;

function groupWord(rtf: string, open: number): string {
  const match = GROUP_WORD.exec(rtf.slice(open, open + 32));
  return match ? match[1].toLowerCase() : '';
}

// The index just past the brace that closes the group opening at `open`.
function groupEnd(rtf: string, open: number): number {
  let depth = 0;

  for (let i = open; i < rtf.length; i += 1) {
    const c = rtf[i];

    // An escaped brace or backslash is text, not structure.
    if (c === '\\') i += 1;
    else if (c === '{') depth += 1;
    else if (c === '}') {
      depth -= 1;
      if (depth === 0) return i + 1;
    }
  }

  return rtf.length;
}

// A group's own words and data, its nested groups (\picprop, \blipuid) set aside. A space is left
// in each one's place, so the control word before it cannot run on into the hex after it.
function withoutNestedGroups(body: string): string {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;

  for (let i = 0; i < body.length; i += 1) {
    const c = body[i];

    if (c === '\\') {
      i += 1;
    } else if (c === '{') {
      if (depth === 0) parts.push(body.slice(start, i));
      depth += 1;
    } else if (c === '}') {
      depth -= 1;
      if (depth === 0) {
        parts.push(' ');
        start = i + 1;
      }
    }
  }

  if (depth === 0) parts.push(body.slice(start));

  return parts.join('');
}

function hexValue(code: number): number {
  if (code <= 57) return code - 48; // 0-9
  if (code >= 97) return code - 87; // a-f
  return code - 55; // A-F
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);

  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = (hexValue(hex.charCodeAt(i * 2)) << 4) | hexValue(hex.charCodeAt(i * 2 + 1));
  }

  return bytes;
}

const startsWith = (bytes: Uint8Array, signature: number[]) =>
  bytes.length >= signature.length && signature.every((byte, i) => bytes[i] === byte);

function readPicture(group: string): IRtfPicture {
  const own = withoutNestedGroups(group.slice(1, -1));
  const size = shownSize(own);
  const unusable: IRtfPicture = { contentType: null, bytes: null, ...size };

  let contentType: IRtfPicture['contentType'] = null;
  if (/\\pngblip(?![a-z])/i.test(own)) contentType = 'image/png';
  else if (/\\jpegblip(?![a-z])/i.test(own)) contentType = 'image/jpeg';

  if (!contentType) return unusable;

  // What is left once the control words go is the picture, as hex split across lines.
  const hex = own.replace(/\\[a-zA-Z]+-?\d* ?/g, '').replace(/[^0-9a-fA-F]/g, '');
  if (!hex.length || hex.length % 2 !== 0) return unusable;

  const bytes = hexToBytes(hex);

  // The control word said one thing; the bytes have to agree before anyone is shown them.
  const signature = contentType === 'image/png' ? [0x89, 0x50, 0x4e, 0x47] : [0xff, 0xd8, 0xff];

  return startsWith(bytes, signature) ? { contentType, bytes, ...size } : unusable;
}

// Word rounds the scale to a whole percent, so the two sizes can differ by a pixel or two.
const sameSize = (a: number, b: number) => Math.abs(a - b) <= Math.max(3, b * 0.03);

/*
  Pair each file: <img>, in document order, with the next picture shown at its size - or with none,
  which drops it. Where either side gives no size, the next picture in order is taken, which is all
  there is to go on.
*/
export function matchRtfPictures(
  images: { width?: number; height?: number }[],
  pictures: IRtfPicture[]
): (IRtfPicture | undefined)[] {
  let next = 0;

  return images.map(({ width, height }) => {
    for (let k = next; k < pictures.length; k += 1) {
      const picture = pictures[k];
      const sized = !!width && !!height && !!picture.width && !!picture.height;

      if (!sized || (sameSize(picture.width!, width!) && sameSize(picture.height!, height!))) {
        next = k + 1;
        return picture;
      }
    }

    return undefined;
  });
}

export default function extractRtfPictures(rtf: string): IRtfPicture[] {
  const pictures: IRtfPicture[] = [];

  let i = 0;

  while (i < rtf.length) {
    const c = rtf[i];

    if (c === '\\') {
      i += 2;
    } else if (c === '{') {
      const word = groupWord(rtf, i);

      if (SKIPPED_GROUPS.includes(word)) {
        i = groupEnd(rtf, i);
      } else if (word === 'pict') {
        const end = groupEnd(rtf, i);
        pictures.push(readPicture(rtf.slice(i, end)));
        i = end;
      } else {
        i += 1;
      }
    } else {
      i += 1;
    }
  }

  return pictures;
}
